/**
 * Futures Dashboard Component Identifiers
 * Standardized unique keys for each modular trading component on the Futures Dashboard.
 */
export const FUTURES_COMPONENTS = {
  ORDER_BOOK: "ORDER_BOOK",         // Derivative Pair Selector + Live Order Book (Asks & Bids) [FutureLeftTable]
  TRADE_HISTORY: "TRADE_HISTORY",   // Market Executed Trades ticker (Time, Amount, Price) [FutureRightTable]
  CHART: "CHART",                   // Derivative TradingView / Depth Chart Toggle Module [FutureChart]
  BUY_SELL: "BUY_SELL",             // Futures Order Placement Forms (Market, Limit, Stop, Leverage) [FutureBuySell]
  BOTTOM_TABLE: "BOTTOM_TABLE",     // User Positions, Open Orders, Trade History & Stop-Limit [FutureBottomTable]
  MARGIN_HOLDINGS: "MARGIN_HOLDINGS", // Futures Isolated/Cross Margin & Holdings Module [FutureMarginHoldings]
  TOP_MOVERS: "TOP_MOVERS",         // 24h Futures Top Movers, Gainers, Losers [FutureTopMovers]
};

/**
 * Metadata for each component for admin display, documentation and layout builders.
 */
export const FUTURES_COMPONENT_META = {
  [FUTURES_COMPONENTS.ORDER_BOOK]: {
    id: FUTURES_COMPONENTS.ORDER_BOOK,
    name: "Order Book & Market Pairs",
    shortName: "Order Book",
    description: "Derivative contract search, market filter dropdown, and live bid/ask order book",
    defaultWidth: { xs: 12, sm: 12, md: 2 },
  },
  [FUTURES_COMPONENTS.TRADE_HISTORY]: {
    id: FUTURES_COMPONENTS.TRADE_HISTORY,
    name: "Market Trade History",
    shortName: "Trade History",
    description: "Real-time stream of executed futures trades with price, volume, and time",
    defaultWidth: { xs: 12, sm: 12, md: 2 },
  },
  [FUTURES_COMPONENTS.CHART]: {
    id: FUTURES_COMPONENTS.CHART,
    name: "Trading Chart (TV & Depth)",
    shortName: "Chart",
    description: "Interactive TradingView candlestick chart for perpetual contracts",
    defaultWidth: { xs: 12, sm: 12, md: 8 },
  },
  [FUTURES_COMPONENTS.BUY_SELL]: {
    id: FUTURES_COMPONENTS.BUY_SELL,
    name: "Futures Order Module",
    shortName: "Buy / Sell",
    description: "Order entry forms: Long/Short, Limit, Market, Stop, and Leverage slider",
    defaultWidth: { xs: 12, sm: 12, md: 8 },
  },
  [FUTURES_COMPONENTS.BOTTOM_TABLE]: {
    id: FUTURES_COMPONENTS.BOTTOM_TABLE,
    name: "User Positions & History",
    shortName: "Positions & Orders",
    description: "Open positions, PnL, liquidation price, open orders, and trade history",
    defaultWidth: { xs: 12, md: 12 },
  },
  [FUTURES_COMPONENTS.MARGIN_HOLDINGS]: {
    id: FUTURES_COMPONENTS.MARGIN_HOLDINGS,
    name: "Margin & Holdings (Isolated / Cross)",
    shortName: "Margin Holdings",
    description: "Isolated and Cross margin balances, Fiat and Crypto holdings with transfer and funding actions",
    defaultWidth: { xs: 12, sm: 12, md: 4 },
  },
  [FUTURES_COMPONENTS.TOP_MOVERS]: {
    id: FUTURES_COMPONENTS.TOP_MOVERS,
    name: "Top Movers & Gainers",
    shortName: "Top Movers",
    description: "24h perpetual gainers, losers, and fluctuation tickers",
    defaultWidth: { xs: 12, sm: 12, md: 5 },
  },
};

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 1: DEFAULT (Paybito Classic 3-Column Standard) ────────────────────
// ══════════════════════════════════════════════════════════════════════════════
export const DEFAULT_FUTURES_LAYOUT_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-orderbook",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 position-relative p-0 orderbooktable",
        components: [{ key: FUTURES_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-chart-buysell",
        size: { xs: 12, sm: 12, md: 8 },
        className: "mb-1 px-1 spotchart",
        components: [
          { key: FUTURES_COMPONENTS.CHART },
          { key: FUTURES_COMPONENTS.BUY_SELL, sx: { mt: "6px" } },
        ],
      },
      {
        colId: "col-tradehistory",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0 orderbooktable",
        components: [{ key: FUTURES_COMPONENTS.TRADE_HISTORY }],
      },
    ],
  },
  {
    rowId: "bottom-history-row",
    columns: [
      {
        colId: "col-bottom-table",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: FUTURES_COMPONENTS.BOTTOM_TABLE, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 2: BINANCE_PRO (Advanced - Margin Holdings in Right Column) ───────
// ══════════════════════════════════════════════════════════════════════════════
export const BINANCE_PRO_FUTURES_LAYOUT_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      // Left Column: Order Book + Trade History stacked (compact width)
      {
        colId: "col-left-orderbook-trades",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0",
        components: [
          { key: FUTURES_COMPONENTS.ORDER_BOOK, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: FUTURES_COMPONENTS.TRADE_HISTORY, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
      // Center Column: Chart + Positions/Orders stacked
      {
        colId: "col-center-chart-orders",
        size: { xs: 12, sm: 12, md: 6 },
        className: "mb-1 px-1",
        components: [
          { key: FUTURES_COMPONENTS.CHART, flex: "1.4 1 0%", height: "calc(58% - 3px)", minHeight: 0 },
          { key: FUTURES_COMPONENTS.BOTTOM_TABLE, flex: "1 1 0%", height: "calc(42% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
      // Right Column: Buy & Sell Order Entry (Top) + Margin & Holdings Module (Bottom)
      {
        colId: "col-right-buysell",
        size: { xs: 12, sm: 12, md: 4 },
        className: "mb-1 p-0",
        components: [
          { key: FUTURES_COMPONENTS.BUY_SELL, flex: "none", height: "calc(50% + 17px)", minHeight: 0 },
          { key: FUTURES_COMPONENTS.MARGIN_HOLDINGS, flex: "none", height: "calc(50% - 23px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 3: CHART_RIGHT_STACKED_BOTTOM ─────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
export const CHART_RIGHT_STACKED_BOTTOM_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-orderbook",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 position-relative p-0 orderbooktable",
        components: [{ key: FUTURES_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-tradehistory-beside-left",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0 orderbooktable",
        components: [{ key: FUTURES_COMPONENTS.TRADE_HISTORY }],
      },
      {
        colId: "col-chart-buysell-right",
        size: { xs: 12, sm: 12, md: 8 },
        className: "mb-1 px-1 spotchart",
        components: [
          { key: FUTURES_COMPONENTS.CHART },
          { key: FUTURES_COMPONENTS.BUY_SELL, sx: { mt: "6px" } },
        ],
      },
    ],
  },
  {
    rowId: "mytrades-row",
    columns: [
      {
        colId: "col-bottom-table-full-width",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: FUTURES_COMPONENTS.BOTTOM_TABLE, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 4: COINBASE_ADVANCED (Order Form Left, Book Center, Chart Right) ──
// ══════════════════════════════════════════════════════════════════════════════
export const COINBASE_ADVANCED_FUTURES_LAYOUT_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      // Left Column: Buy & Sell Order Entry (Top) + Margin Holdings (Bottom)
      {
        colId: "col-left-buysell",
        size: { xs: 12, sm: 12, md: 4 },
        className: "mb-1 p-0",
        components: [
          { key: FUTURES_COMPONENTS.BUY_SELL, flex: "none", height: "calc(50% + 17px)", minHeight: 0 },
          { key: FUTURES_COMPONENTS.MARGIN_HOLDINGS, flex: "none", height: "calc(50% - 23px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
      // Center-Left Column: Order Book & Trades (compact width)
      {
        colId: "col-center-orderbook",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 px-1",
        components: [
          { key: FUTURES_COMPONENTS.ORDER_BOOK, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: FUTURES_COMPONENTS.TRADE_HISTORY, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
      // Right Column: Chart on top, Positions/Orders stacked below
      {
        colId: "col-right-chart-trades",
        size: { xs: 12, sm: 12, md: 6 },
        className: "mb-1 p-0",
        components: [
          { key: FUTURES_COMPONENTS.CHART, flex: "1.4 1 0%", height: "calc(58% - 3px)", minHeight: 0 },
          { key: FUTURES_COMPONENTS.BOTTOM_TABLE, flex: "1 1 0%", height: "calc(42% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 5: FOUR_COLUMN_PANORAMIC (Ultra-Wide Pro Execution Grid) ──────────
// ══════════════════════════════════════════════════════════════════════════════
export const FOUR_COLUMN_PANORAMIC_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-orderbook",
        size: { xs: 12, sm: 6, md: 2 },
        className: "mb-1 position-relative p-0 four-col-orderbook",
        components: [{ key: FUTURES_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-tradehistory",
        size: { xs: 12, sm: 6, md: 2 },
        className: "mb-1 p-0 four-col-trades",
        components: [{ key: FUTURES_COMPONENTS.TRADE_HISTORY, props: { hideTopMovers: true } }],
      },
      {
        colId: "col-chart",
        size: { xs: 12, sm: 12, md: 5 },
        className: "mb-1 px-1 four-col-chart",
        components: [
          { key: FUTURES_COMPONENTS.CHART, flex: 1, height: "calc(100% - 216px)", minHeight: 400 },
          { key: FUTURES_COMPONENTS.TOP_MOVERS, flex: "none", height: "210px", minHeight: 0, maxHeight: "210px", sx: { mt: "6px" }, props: { standalone: true } },
        ],
      },
      {
        colId: "col-buysell-side",
        size: { xs: 12, sm: 12, md: 3 },
        className: "mb-1 p-0 four-col-buysell",
        components: [
          { key: FUTURES_COMPONENTS.BUY_SELL, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: FUTURES_COMPONENTS.MARGIN_HOLDINGS, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
    ],
  },
  {
    rowId: "bottom-history-row",
    columns: [
      {
        colId: "col-bottom-table",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: FUTURES_COMPONENTS.BOTTOM_TABLE, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 6: CHART_LEFT_INVERTED (Chart on Left, Order Book on Right) ───────
// ══════════════════════════════════════════════════════════════════════════════
export const CHART_LEFT_INVERTED_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-chart-buysell-left",
        size: { xs: 12, sm: 12, md: 8 },
        className: "mb-1 px-1 spotchart",
        components: [
          { key: FUTURES_COMPONENTS.CHART },
          { key: FUTURES_COMPONENTS.BUY_SELL, sx: { mt: "6px" } },
        ],
      },
      {
        colId: "col-orderbook-right",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 position-relative p-0 orderbooktable",
        components: [{ key: FUTURES_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-tradehistory-right",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0 orderbooktable",
        components: [{ key: FUTURES_COMPONENTS.TRADE_HISTORY }],
      },
    ],
  },
  {
    rowId: "bottom-history-row",
    columns: [
      {
        colId: "col-bottom-table",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: FUTURES_COMPONENTS.BOTTOM_TABLE, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 7: SWAPPED_BOTTOM_CENTER (Chart + History Center, Buy/Sell Bottom)─
// ══════════════════════════════════════════════════════════════════════════════
export const SWAPPED_BOTTOM_CENTER_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-orderbook",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 position-relative p-0 orderbooktable",
        components: [{ key: FUTURES_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-tradehistory-beside-left",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0 orderbooktable",
        components: [{ key: FUTURES_COMPONENTS.TRADE_HISTORY }],
      },
      {
        colId: "col-chart-right",
        size: { xs: 12, sm: 12, md: 8 },
        className: "mb-1 px-1 spotchart",
        components: [
          { key: FUTURES_COMPONENTS.CHART, flex: "1.4 1 0%", height: "calc(58% - 3px)", minHeight: 0 },
          { key: FUTURES_COMPONENTS.BOTTOM_TABLE, flex: "1 1 0%", height: "calc(42% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
    ],
  },
  {
    rowId: "bottom-swapped-row",
    columns: [
      {
        colId: "col-buysell-full-width",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: FUTURES_COMPONENTS.BUY_SELL, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET REGISTRY & METADATA ────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
export const FUTURES_LAYOUT_PRESETS = {
  DEFAULT: DEFAULT_FUTURES_LAYOUT_MATRIX,
  BINANCE_PRO: BINANCE_PRO_FUTURES_LAYOUT_MATRIX,
  CHART_RIGHT_STACKED_BOTTOM: CHART_RIGHT_STACKED_BOTTOM_MATRIX,
  COINBASE_ADVANCED: COINBASE_ADVANCED_FUTURES_LAYOUT_MATRIX,
  FOUR_COLUMN_PANORAMIC: FOUR_COLUMN_PANORAMIC_MATRIX,
  CHART_LEFT_INVERTED: CHART_LEFT_INVERTED_MATRIX,
  SWAPPED_BOTTOM_CENTER: SWAPPED_BOTTOM_CENTER_MATRIX,
};

export const FUTURES_LAYOUT_PRESET_LIST = [
  {
    id: "DEFAULT",
    settingId: 1,
    name: "Classic",
    description: "Left OrderBook | Center Chart & Buy/Sell | Right Trade History | Bottom Table",
    matrix: DEFAULT_FUTURES_LAYOUT_MATRIX,
  },
  {
    id: "BINANCE_PRO",
    settingId: 2,
    name: "Advanced",
    description: "Left OrderBook & Trades | Center Chart & History | Right Buy/Sell & Margin Holdings",
    matrix: BINANCE_PRO_FUTURES_LAYOUT_MATRIX,
  },
  {
    id: "CHART_RIGHT_STACKED_BOTTOM",
    settingId: 3,
    name: "Chart Right",
    description: "Left OrderBook & Trades | Right Chart & Buy/Sell | Bottom Positions & Orders",
    matrix: CHART_RIGHT_STACKED_BOTTOM_MATRIX,
  },
  {
    id: "COINBASE_ADVANCED",
    settingId: 4,
    name: "Pro Trader",
    description: "Left Buy/Sell & Margin Holdings | Center OrderBook & Trades | Right Chart & Positions",
    matrix: COINBASE_ADVANCED_FUTURES_LAYOUT_MATRIX,
  },
  {
    id: "FOUR_COLUMN_PANORAMIC",
    settingId: 5,
    name: "4-Column",
    description: "Col 1 OrderBook | Col 2 Trades | Col 3 Big Chart | Col 4 Buy/Sell & Margin Holdings",
    matrix: FOUR_COLUMN_PANORAMIC_MATRIX,
  },
  {
    id: "CHART_LEFT_INVERTED",
    settingId: 6,
    name: "Chart Left",
    description: "Left Big Chart & Buy/Sell | Right OrderBook & Trade History | Bottom Table",
    matrix: CHART_LEFT_INVERTED_MATRIX,
  },
  {
    id: "SWAPPED_BOTTOM_CENTER",
    settingId: 7,
    name: "Swapped",
    description: "Left 2 Tables | Center Chart & Positions | Full-Width Buy/Sell at Bottom",
    matrix: SWAPPED_BOTTOM_CENTER_MATRIX,
  },
];

/**
 * Global active matrix currently used by the futures dashboard.
 */
export let ACTIVE_FUTURES_LAYOUT_MATRIX = DEFAULT_FUTURES_LAYOUT_MATRIX;

/**
 * Retrieve the current layout matrix directly reflecting the Spot dashboard layout
 */
export const getFuturesLayoutMatrix = () => {
  try {
    // Strictly reflect the layout set in the spot dashboard
    const spotPresetId = localStorage.getItem("admin_spot_layout_preset") || "DEFAULT";
    if (spotPresetId && FUTURES_LAYOUT_PRESETS[spotPresetId]) {
      return FUTURES_LAYOUT_PRESETS[spotPresetId];
    }
  } catch (err) {
    console.error("Error reading spot layout preset for futures:", err);
  }
  return DEFAULT_FUTURES_LAYOUT_MATRIX;
};

/**
 * Switch preset and broadcast to the dashboard in real-time
 */
export const setFuturesLayoutPreset = (presetId) => {
  if (FUTURES_LAYOUT_PRESETS[presetId]) {
    ACTIVE_FUTURES_LAYOUT_MATRIX = FUTURES_LAYOUT_PRESETS[presetId];
    try {
      localStorage.setItem("admin_futures_layout_preset", presetId);
      localStorage.removeItem("admin_futures_layout_matrix");
    } catch (e) {
      console.error(e);
    }
    window.dispatchEvent(
      new CustomEvent("futures_layout_change", { detail: FUTURES_LAYOUT_PRESETS[presetId] })
    );
    return true;
  }
  return false;
};

/**
 * Save custom matrix
 */
export const saveFuturesLayoutMatrix = (matrix) => {
  ACTIVE_FUTURES_LAYOUT_MATRIX = matrix;
  try {
    localStorage.setItem("admin_futures_layout_matrix", JSON.stringify(matrix));
    localStorage.removeItem("admin_futures_layout_preset");
  } catch (err) {
    console.error("Error saving futures layout matrix:", err);
  }
  window.dispatchEvent(new CustomEvent("futures_layout_change", { detail: matrix }));
};
